/**
 * P1-006 Agentic PM Level 3 Dashboard - Operational PM Assistant
 * 
 * The UI that answers: "What should I do today?"
 * 
 * Dashboard Sections:
 * 1. Today's Priorities - Critical and high-priority items requiring attention
 * 2. Recommended Actions - AI-generated recommendations awaiting approval
 * 3. Capacity Risks - Resource bottlenecks and overallocations
 * 4. Benefit Risks - Outcomes at risk of not being realized
 * 5. Governance Risks - Overdue or aging governance actions
 * 6. Intent Drift - Items drifting from original approved intent
 * 7. Executive Attention Required - Items needing executive intervention
 * 
 * Architecture Position: Intent → Decision → Governance → Delivery → Outcome → Resource → AGENTIC PM
 * 
 * Key Principle:
 *   "Agentic PM RECOMMENDS but NEVER autonomously executes governed actions.
 *    All significant actions require human approval."
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Brain, AlertTriangle, CheckCircle, Clock, TrendingUp,
  TrendingDown, Loader2, Activity, Zap, Gauge, Target,
  BarChart3, RefreshCw, AlertCircle, ChevronRight, FileText,
  Users, Shield, Lightbulb, XCircle, CheckCircle2, ThumbsUp,
  ThumbsDown, Calendar, ArrowRight, Eye, MessageSquare,
  Scale, Layers, Award, CircleDot, Rocket, Flag, Flame
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, RadialBarChart, RadialBar, PieChart, Pie, Cell
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  const tenantId =
    localStorage.getItem('impersonated_tenant_id') ||
    localStorage.getItem('simpliview_user_tenant_id') ||
    localStorage.getItem('simpliview_tenant_id');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantId ? { 'X-Tenant-ID': tenantId } : {})
  };
};

// =============================================================================
// STATUS HELPERS
// =============================================================================

const getSeverityColor = (severity) => {
  switch (severity) {
    case 'critical': return 'text-red-600 bg-red-50 border-red-200';
    case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
    case 'medium': return 'text-amber-600 bg-amber-50 border-amber-200';
    case 'low': return 'text-blue-600 bg-blue-50 border-blue-200';
    default: return 'text-slate-600 bg-slate-50 border-slate-200';
  }
};

const getSeverityBadge = (severity) => {
  const colors = {
    critical: 'bg-red-100 text-red-800 border-red-300',
    high: 'bg-orange-100 text-orange-800 border-orange-300',
    medium: 'bg-amber-100 text-amber-800 border-amber-300',
    low: 'bg-blue-100 text-blue-800 border-blue-300'
  };
  return colors[severity] || 'bg-slate-100 text-slate-800 border-slate-300';
};

const getPriorityIcon = (priority) => {
  switch (priority) {
    case 'critical': return <Flame className="w-4 h-4 text-red-500" />;
    case 'high': return <AlertTriangle className="w-4 h-4 text-orange-500" />;
    case 'medium': return <Flag className="w-4 h-4 text-amber-500" />;
    default: return <CircleDot className="w-4 h-4 text-blue-500" />;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'critical': return 'text-red-600';
    case 'attention_required': return 'text-orange-600';
    case 'monitoring': return 'text-amber-600';
    case 'healthy': return 'text-emerald-600';
    default: return 'text-slate-600';
  }
};

const getStatusBg = (status) => {
  switch (status) {
    case 'critical': return 'bg-red-500';
    case 'attention_required': return 'bg-orange-500';
    case 'monitoring': return 'bg-amber-500';
    case 'healthy': return 'bg-emerald-500';
    default: return 'bg-slate-500';
  }
};

// =============================================================================
// MAIN DASHBOARD COMPONENT
// =============================================================================

export default function AgenticPMDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [brief, setBrief] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [processingProposal, setProcessingProposal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  // =========================================================================
  // DATA FETCHING
  // =========================================================================

  const fetchDashboardData = useCallback(async () => {
    try {
      const [dashRes, briefRes, proposalsRes, assessmentRes] = await Promise.all([
        axios.get(`${API_BASE}/pm-assistant/dashboard`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/pm-assistant/daily-brief`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/pm-assistant/proposals`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/pm-assistant/assessment`, { headers: getAuthHeaders() })
      ]);

      setDashboardData(dashRes.data?.dashboard);
      setBrief(briefRes.data?.brief);
      setProposals(proposalsRes.data?.proposals || []);
      setAssessment(assessmentRes.data?.assessment);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load Agentic PM Dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
  };

  // =========================================================================
  // PROPOSAL ACTIONS
  // =========================================================================

  const handleApproveProposal = async (proposalId) => {
    setProcessingProposal(proposalId);
    try {
      await axios.post(
        `${API_BASE}/pm-assistant/proposals/${proposalId}/approve`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Proposal approved successfully');
      fetchDashboardData();
    } catch (error) {
      console.error('Failed to approve proposal:', error);
      toast.error('Failed to approve proposal');
    } finally {
      setProcessingProposal(null);
    }
  };

  const handleRejectProposal = async () => {
    if (!selectedProposal || rejectReason.length < 10) {
      toast.error('Please provide a reason (min 10 characters)');
      return;
    }
    setProcessingProposal(selectedProposal.proposal_id);
    try {
      await axios.post(
        `${API_BASE}/pm-assistant/proposals/${selectedProposal.proposal_id}/reject`,
        { reason: rejectReason },
        { headers: getAuthHeaders() }
      );
      toast.success('Proposal rejected');
      setShowRejectModal(false);
      setSelectedProposal(null);
      setRejectReason('');
      fetchDashboardData();
    } catch (error) {
      console.error('Failed to reject proposal:', error);
      toast.error('Failed to reject proposal');
    } finally {
      setProcessingProposal(null);
    }
  };

  // =========================================================================
  // RENDER LOADING STATE
  // =========================================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-violet-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400 text-lg">Loading Agentic PM Intelligence...</p>
        </div>
      </div>
    );
  }

  const summary = brief?.summary || {};
  const sections = brief?.sections || {};
  const overallStatus = brief?.overall_status || 'healthy';

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-violet-500/20 rounded-xl border border-violet-500/30">
              <Brain className="w-8 h-8 text-violet-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Agentic PM Dashboard</h1>
              <p className="text-slate-400 text-sm">
                Level 3 Operational Assistant • What should I focus on today?
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Overall Status Indicator */}
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
              overallStatus === 'critical' ? 'bg-red-500/10 border-red-500/30 text-red-400' :
              overallStatus === 'attention_required' ? 'bg-orange-500/10 border-orange-500/30 text-orange-400' :
              overallStatus === 'monitoring' ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' :
              'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
            }`}>
              <div className={`w-2 h-2 rounded-full ${getStatusBg(overallStatus)} animate-pulse`} />
              <span className="font-medium capitalize">{overallStatus.replace('_', ' ')}</span>
            </div>
            
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg border border-slate-700 text-slate-400 hover:text-white transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 bg-slate-800/50 p-1 rounded-lg border border-slate-700/50 w-fit">
          {[
            { id: 'overview', label: 'Overview', icon: Eye },
            { id: 'actions', label: 'Recommended Actions', icon: Lightbulb },
            { id: 'risks', label: 'Risk Intelligence', icon: AlertTriangle },
            { id: 'assessment', label: 'PM Assessment', icon: Gauge }
          ].map(tab => (
            <button
              key={tab.id}
              data-testid={`apm-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-violet-600 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ================================================================= */}
        {/* OVERVIEW TAB */}
        {/* ================================================================= */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <SummaryCard
                icon={AlertCircle}
                label="Executive Attention"
                value={summary.executive_attention_count || 0}
                color="red"
              />
              <SummaryCard
                icon={Users}
                label="Capacity Risks"
                value={summary.capacity_risks_count || 0}
                color="orange"
              />
              <SummaryCard
                icon={Shield}
                label="Governance Risks"
                value={summary.governance_risks_count || 0}
                color="amber"
              />
              <SummaryCard
                icon={Target}
                label="Outcome Risks"
                value={summary.outcome_risks_count || 0}
                color="purple"
              />
              <SummaryCard
                icon={CheckCircle}
                label="Recent Wins"
                value={summary.wins_count || 0}
                color="emerald"
              />
              <SummaryCard
                icon={Lightbulb}
                label="Recommendations"
                value={summary.recommendations_count || 0}
                color="violet"
              />
            </div>

            {/* Critical & High Priority Items */}
            {(summary.critical_items > 0 || summary.high_priority_items > 0) && (
              <div className="bg-slate-800/50 rounded-xl border border-red-500/30 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-red-500/20 rounded-lg">
                    <Flame className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">Items Requiring Immediate Attention</h2>
                    <p className="text-sm text-slate-400">
                      {summary.critical_items} critical, {summary.high_priority_items} high priority
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  {sections.executive_attention?.slice(0, 3).map((item, idx) => (
                    <RiskItem key={idx} item={item} />
                  ))}
                  {sections.capacity_risks?.filter(r => r.severity === 'critical' || r.severity === 'high').slice(0, 2).map((item, idx) => (
                    <RiskItem key={`cap-${idx}`} item={item} />
                  ))}
                </div>
              </div>
            )}

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Today's Priorities */}
              <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-violet-500/20 rounded-lg">
                    <Target className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">Today&apos;s Priorities</h2>
                    <p className="text-sm text-slate-400">What should I focus on today?</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {sections.executive_attention?.length > 0 ? (
                    sections.executive_attention.slice(0, 4).map((item, idx) => (
                      <PriorityItem key={idx} item={item} priority={1} />
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-2" />
                      <p className="text-slate-400">No critical items requiring attention</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Wins */}
              <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-emerald-500/20 rounded-lg">
                    <Award className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">Recent Wins</h2>
                    <p className="text-sm text-slate-400">Completed milestones and achievements</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {sections.wins?.length > 0 ? (
                    sections.wins.slice(0, 4).map((item, idx) => (
                      <WinItem key={idx} item={item} />
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <Rocket className="w-12 h-12 text-slate-600 mx-auto mb-2" />
                      <p className="text-slate-400">No recent wins to display</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Pending Proposals */}
            {proposals.length > 0 && (
              <div className="bg-slate-800/50 rounded-xl border border-violet-500/30 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-violet-500/20 rounded-lg">
                      <Lightbulb className="w-5 h-5 text-violet-400" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-white">Pending Approval</h2>
                      <p className="text-sm text-slate-400">{proposals.length} recommendations awaiting your decision</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveTab('actions')}
                    className="text-sm text-violet-400 hover:text-violet-300 flex items-center gap-1"
                  >
                    View All <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {proposals.slice(0, 4).map((proposal) => (
                    <ProposalCard
                      key={proposal.proposal_id}
                      proposal={proposal}
                      onApprove={() => handleApproveProposal(proposal.proposal_id)}
                      onReject={() => {
                        setSelectedProposal(proposal);
                        setShowRejectModal(true);
                      }}
                      processing={processingProposal === proposal.proposal_id}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ================================================================= */}
        {/* ACTIONS TAB */}
        {/* ================================================================= */}
        {activeTab === 'actions' && (
          <div className="space-y-6">
            <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-violet-500/20 rounded-lg">
                  <Lightbulb className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Recommended Actions</h2>
                  <p className="text-sm text-slate-400">
                    AI-generated recommendations requiring human approval
                  </p>
                </div>
              </div>

              {proposals.length > 0 ? (
                <div className="space-y-4">
                  {proposals.map((proposal) => (
                    <ProposalDetailCard
                      key={proposal.proposal_id}
                      proposal={proposal}
                      onApprove={() => handleApproveProposal(proposal.proposal_id)}
                      onReject={() => {
                        setSelectedProposal(proposal);
                        setShowRejectModal(true);
                      }}
                      processing={processingProposal === proposal.proposal_id}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">All Caught Up!</h3>
                  <p className="text-slate-400">No pending recommendations at this time</p>
                </div>
              )}
            </div>

            {/* Governance Boundary Info */}
            <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <Shield className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Governance Boundary</h2>
                  <p className="text-sm text-slate-400">What Agentic PM can and cannot do</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-emerald-400 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> Permitted Actions
                  </h3>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-2" />
                      Generate reports and daily briefs (autonomous)
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-2" />
                      Monitor deviations and create alerts (autonomous)
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2" />
                      Recommend escalations (requires approval)
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2" />
                      Propose resource reallocations (requires approval)
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-red-400 mb-3 flex items-center gap-2">
                    <XCircle className="w-4 h-4" /> Prohibited Actions
                  </h3>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2" />
                      Approve budgets (financial authority)
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2" />
                      Approve scope changes (contractual impact)
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2" />
                      Close governance reviews (compliance)
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2" />
                      Override human decisions (accountability)
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ================================================================= */}
        {/* RISKS TAB */}
        {/* ================================================================= */}
        {activeTab === 'risks' && (
          <div className="space-y-6">
            {/* Capacity Risks */}
            <RiskSection
              title="Capacity Risks"
              subtitle="Resource bottlenecks and overallocations"
              icon={Users}
              iconColor="orange"
              items={sections.capacity_risks || []}
              emptyMessage="No capacity risks detected"
            />

            {/* Governance Risks */}
            <RiskSection
              title="Governance Risks"
              subtitle="Overdue or aging governance actions"
              icon={Shield}
              iconColor="amber"
              items={sections.governance_risks || []}
              emptyMessage="No governance risks detected"
            />

            {/* Outcome Risks */}
            <RiskSection
              title="Outcome Risks"
              subtitle="Benefits at risk of not being realized"
              icon={Target}
              iconColor="purple"
              items={sections.outcome_risks || []}
              emptyMessage="No outcome risks detected"
            />
          </div>
        )}

        {/* ================================================================= */}
        {/* ASSESSMENT TAB */}
        {/* ================================================================= */}
        {activeTab === 'assessment' && assessment && (
          <div className="space-y-6">
            {/* Overall Score */}
            <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-violet-500/20 rounded-lg">
                    <Gauge className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">Agentic PM L3 Assessment</h2>
                    <p className="text-sm text-slate-400">Intelligence, Coordination, and Governance scores</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-white">{assessment.overall_score?.toFixed(1)}</div>
                  <div className={`text-sm font-medium ${
                    assessment.overall_level === 'EXCELLENT' ? 'text-emerald-400' :
                    assessment.overall_level === 'GOOD' ? 'text-blue-400' :
                    assessment.overall_level === 'ADEQUATE' ? 'text-amber-400' :
                    'text-red-400'
                  }`}>{assessment.overall_level}</div>
                </div>
              </div>

              {/* Score Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <ScoreCard
                  title="PM Intelligence"
                  subtitle="Can it reason?"
                  score={assessment.dimensions?.intelligence?.score || 0}
                  level={assessment.dimensions?.intelligence?.level || 'N/A'}
                  capabilities={assessment.dimensions?.intelligence?.capabilities || []}
                  gaps={assessment.dimensions?.intelligence?.gaps || []}
                  color="violet"
                />
                <ScoreCard
                  title="PM Coordination"
                  subtitle="Can it coordinate?"
                  score={assessment.dimensions?.coordination?.score || 0}
                  level={assessment.dimensions?.coordination?.level || 'N/A'}
                  capabilities={assessment.dimensions?.coordination?.capabilities || []}
                  gaps={assessment.dimensions?.coordination?.gaps || []}
                  color="blue"
                />
                <ScoreCard
                  title="PM Governance"
                  subtitle="Can it operate safely?"
                  score={assessment.dimensions?.governance?.score || 0}
                  level={assessment.dimensions?.governance?.level || 'N/A'}
                  capabilities={assessment.dimensions?.governance?.capabilities || []}
                  guardrails={assessment.dimensions?.governance?.guardrails || []}
                  color="emerald"
                />
              </div>
            </div>

            {/* One-Day Absence Test */}
            {assessment.one_day_absence_test && (
              <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-amber-500/20 rounded-lg">
                    <Calendar className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">One-Day Absence Test</h2>
                    <p className="text-sm text-slate-400">{assessment.one_day_absence_test.question}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Can Perform */}
                  <div className="bg-emerald-500/10 rounded-lg border border-emerald-500/30 p-4">
                    <h3 className="text-sm font-medium text-emerald-400 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> Can Perform Autonomously
                    </h3>
                    <ul className="space-y-2">
                      {(assessment.one_day_absence_test.permitted_work || []).map((item, idx) => (
                        <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-2 shrink-0" />
                          {typeof item === 'string' ? item : item.action}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Can Recommend */}
                  <div className="bg-amber-500/10 rounded-lg border border-amber-500/30 p-4">
                    <h3 className="text-sm font-medium text-amber-400 mb-3 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" /> Requires Human Approval
                    </h3>
                    <ul className="space-y-2">
                      <li className="text-sm text-slate-300 flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0" />
                        Escalate issues to management
                      </li>
                      <li className="text-sm text-slate-300 flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0" />
                        Recommend resource reallocation
                      </li>
                      <li className="text-sm text-slate-300 flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0" />
                        Propose timeline modifications
                      </li>
                      <li className="text-sm text-slate-300 flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0" />
                        Recommend benefit reviews
                      </li>
                    </ul>
                  </div>

                  {/* Cannot Perform */}
                  <div className="bg-red-500/10 rounded-lg border border-red-500/30 p-4">
                    <h3 className="text-sm font-medium text-red-400 mb-3 flex items-center gap-2">
                      <XCircle className="w-4 h-4" /> Cannot Perform
                    </h3>
                    <ul className="space-y-2">
                      {(assessment.one_day_absence_test.prohibited_work || []).map((item, idx) => (
                        <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 shrink-0" />
                          {typeof item === 'string' ? item : item.action}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-700">
                  <p className="text-sm text-slate-400">
                    <span className="font-medium text-white">Verdict:</span>{' '}
                    {assessment.overall_level === 'EXCELLENT' || assessment.overall_level === 'GOOD' ? (
                      <span className="text-emerald-400">
                        Agentic PM has successfully evolved to Level 3 - Operational PM Assistant
                      </span>
                    ) : (
                      <span className="text-amber-400">
                        Agentic PM is developing towards Level 3 capabilities
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && selectedProposal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-2">Reject Proposal</h3>
            <p className="text-sm text-slate-400 mb-4">
              Please provide a reason for rejecting this recommendation.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason (min 10 characters)..."
              className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
              rows={3}
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedProposal(null);
                  setRejectReason('');
                }}
                className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectProposal}
                disabled={rejectReason.length < 10 || processingProposal}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {processingProposal ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ThumbsDown className="w-4 h-4" />
                )}
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function SummaryCard({ icon: Icon, label, value, color }) {
  const colorClasses = {
    red: 'bg-red-500/20 border-red-500/30 text-red-400',
    orange: 'bg-orange-500/20 border-orange-500/30 text-orange-400',
    amber: 'bg-amber-500/20 border-amber-500/30 text-amber-400',
    emerald: 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400',
    violet: 'bg-violet-500/20 border-violet-500/30 text-violet-400',
    purple: 'bg-purple-500/20 border-purple-500/30 text-purple-400',
    blue: 'bg-blue-500/20 border-blue-500/30 text-blue-400'
  };

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
      <div className={`inline-flex p-2 rounded-lg ${colorClasses[color]} mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-sm text-slate-400">{label}</div>
    </div>
  );
}

function RiskItem({ item }) {
  return (
    <div className={`p-4 rounded-lg border ${getSeverityColor(item.severity)}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {getPriorityIcon(item.severity)}
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${getSeverityBadge(item.severity)}`}>
              {item.severity?.toUpperCase()}
            </span>
            <span className="text-xs text-slate-500">{item.type?.replace(/_/g, ' ')}</span>
          </div>
          <h3 className="text-sm font-medium text-slate-900">{item.title}</h3>
          {item.recommendation && (
            <p className="text-xs text-slate-600 mt-1 flex items-center gap-1">
              <Lightbulb className="w-3 h-3" />
              {item.recommendation}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function PriorityItem({ item, priority }) {
  return (
    <div className="flex items-start gap-3 p-3 bg-slate-700/30 rounded-lg border border-slate-700/50">
      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-violet-500/20 text-violet-400 text-xs font-bold shrink-0">
        {priority}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getSeverityBadge(item.severity)}`}>
            {item.severity?.toUpperCase()}
          </span>
        </div>
        <h3 className="text-sm font-medium text-white truncate">{item.title}</h3>
        {item.recommendation && (
          <p className="text-xs text-slate-400 mt-1">{item.recommendation}</p>
        )}
      </div>
    </div>
  );
}

function WinItem({ item }) {
  return (
    <div className="flex items-start gap-3 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
      <div className="p-1.5 bg-emerald-500/20 rounded-lg shrink-0">
        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-medium text-white truncate">{item.title}</h3>
        {item.description && (
          <p className="text-xs text-slate-400 mt-1">{item.description}</p>
        )}
      </div>
    </div>
  );
}

function ProposalCard({ proposal, onApprove, onReject, processing }) {
  return (
    <div className="bg-slate-700/30 rounded-lg border border-slate-700/50 p-4">
      <div className="flex items-start gap-3 mb-3">
        <div className="p-2 bg-violet-500/20 rounded-lg shrink-0">
          <Lightbulb className="w-4 h-4 text-violet-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getSeverityBadge(proposal.priority)}`}>
              {proposal.priority?.toUpperCase()}
            </span>
            <span className="text-xs text-slate-500">{proposal.type?.replace(/_/g, ' ')}</span>
          </div>
          <h3 className="text-sm font-medium text-white truncate">{proposal.title}</h3>
        </div>
      </div>
      <p className="text-xs text-slate-400 mb-3 line-clamp-2">{proposal.reason}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500">
          Authority: {proposal.approval_required_from}
        </span>
        <div className="flex gap-2">
          <button
            onClick={onReject}
            disabled={processing}
            className="p-1.5 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors disabled:opacity-50"
          >
            <ThumbsDown className="w-4 h-4" />
          </button>
          <button
            onClick={onApprove}
            disabled={processing}
            className="p-1.5 text-emerald-400 hover:bg-emerald-500/20 rounded-lg transition-colors disabled:opacity-50"
          >
            {processing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ThumbsUp className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProposalDetailCard({ proposal, onApprove, onReject, processing }) {
  return (
    <div className="bg-slate-700/30 rounded-lg border border-slate-700/50 p-5">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-violet-500/20 rounded-lg shrink-0">
            <Lightbulb className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getSeverityBadge(proposal.priority)}`}>
                {proposal.priority?.toUpperCase()}
              </span>
              <span className="text-xs text-slate-500 bg-slate-600/50 px-2 py-0.5 rounded-full">
                {proposal.type?.replace(/_/g, ' ')}
              </span>
            </div>
            <h3 className="text-base font-medium text-white">{proposal.title}</h3>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={onReject}
            disabled={processing}
            className="px-3 py-1.5 text-red-400 hover:bg-red-500/20 rounded-lg border border-red-500/30 transition-colors disabled:opacity-50 flex items-center gap-1.5 text-sm"
          >
            <ThumbsDown className="w-4 h-4" />
            Reject
          </button>
          <button
            onClick={onApprove}
            disabled={processing}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5 text-sm"
          >
            {processing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ThumbsUp className="w-4 h-4" />
            )}
            Approve
          </button>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <span className="text-xs text-slate-500 uppercase tracking-wider">Reason</span>
          <p className="text-sm text-slate-300 mt-1">{proposal.reason}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-3 border-t border-slate-700">
          <div>
            <span className="text-xs text-slate-500">Entity Type</span>
            <p className="text-sm text-white">{proposal.entity_type}</p>
          </div>
          <div>
            <span className="text-xs text-slate-500">Authority Level</span>
            <p className="text-sm text-white">Level {proposal.authority_level}</p>
          </div>
          <div>
            <span className="text-xs text-slate-500">Approval From</span>
            <p className="text-sm text-white">{proposal.approval_required_from}</p>
          </div>
          <div>
            <span className="text-xs text-slate-500">Expires</span>
            <p className="text-sm text-white">
              {proposal.expires_at ? new Date(proposal.expires_at).toLocaleDateString() : 'N/A'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function RiskSection({ title, subtitle, icon: Icon, iconColor, items, emptyMessage }) {
  const iconColorClasses = {
    orange: 'bg-orange-500/20 text-orange-400',
    amber: 'bg-amber-500/20 text-amber-400',
    purple: 'bg-purple-500/20 text-purple-400',
    red: 'bg-red-500/20 text-red-400'
  };

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 rounded-lg ${iconColorClasses[iconColor]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <p className="text-sm text-slate-400">{subtitle}</p>
        </div>
      </div>

      {items.length > 0 ? (
        <div className="space-y-3">
          {items.map((item, idx) => (
            <RiskItem key={idx} item={item} />
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-2" />
          <p className="text-slate-400">{emptyMessage}</p>
        </div>
      )}
    </div>
  );
}

function ScoreCard({ title, subtitle, score, level, capabilities, gaps, guardrails, color }) {
  const colorClasses = {
    violet: { bg: 'bg-violet-500/20', border: 'border-violet-500/30', text: 'text-violet-400' },
    blue: { bg: 'bg-blue-500/20', border: 'border-blue-500/30', text: 'text-blue-400' },
    emerald: { bg: 'bg-emerald-500/20', border: 'border-emerald-500/30', text: 'text-emerald-400' }
  };

  const c = colorClasses[color];

  return (
    <div className={`${c.bg} rounded-xl border ${c.border} p-5`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className={`text-sm font-medium ${c.text}`}>{title}</h3>
          <p className="text-xs text-slate-400">{subtitle}</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-white">{score}</div>
          <div className={`text-xs font-medium ${c.text}`}>{level}</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-2 bg-slate-700 rounded-full mb-4">
        <div
          className={`h-full rounded-full transition-all ${c.bg.replace('/20', '')}`}
          style={{ width: `${score}%` }}
        />
      </div>

      {/* Capabilities */}
      {capabilities && capabilities.length > 0 && (
        <div className="mb-3">
          <span className="text-xs text-slate-500 uppercase tracking-wider">Capabilities</span>
          <ul className="mt-2 space-y-1">
            {capabilities.slice(0, 4).map((cap, idx) => (
              <li key={idx} className="text-xs text-slate-300 flex items-center gap-2">
                <CheckCircle className={`w-3 h-3 ${c.text}`} />
                {cap}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Gaps */}
      {gaps && gaps.length > 0 && (
        <div className="mb-3">
          <span className="text-xs text-slate-500 uppercase tracking-wider">Gaps</span>
          <ul className="mt-2 space-y-1">
            {gaps.slice(0, 3).map((gap, idx) => (
              <li key={idx} className="text-xs text-slate-400 flex items-center gap-2">
                <XCircle className="w-3 h-3 text-amber-400" />
                {gap}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Guardrails */}
      {guardrails && guardrails.length > 0 && (
        <div>
          <span className="text-xs text-slate-500 uppercase tracking-wider">Guardrails</span>
          <ul className="mt-2 space-y-1">
            {guardrails.slice(0, 4).map((gr, idx) => (
              <li key={idx} className="text-xs text-slate-300 flex items-center gap-2">
                <Shield className={`w-3 h-3 ${c.text}`} />
                {gr}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
